import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";
import { tx, txv } from "../lib/tx";

/**
 * "Read while you wait" summary for the malware start screen. Three short
 * slides (what/who, the three escalating asks and the fake repair, key
 * definitions) shown in the reader while the 3D room downloads. Content only;
 * the reader UI lives in ui/StartReader.tsx and the shared art toolkit in
 * ui/readingArt.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a game page in the browser asking to send notifications          */
/* ------------------------------------------------------------------ */
function PermissionArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Example: a game page in the browser")} aria={tx("A browser showing a news-site game ad and a prompt asking to allow notifications, with Block highlighted")} height={H}>
      <circle cx="24" cy="44" r="3.5" fill={C.line} />
      <circle cx="36" cy="44" r="3.5" fill={C.line} />
      <circle cx="48" cy="44" r="3.5" fill={C.line} />
      <rect x="62.5" y="33.5" width="281" height="21" rx="10.5" fill={C.panel2} stroke={C.line} />
      <text x="76" y="48" fontSize="10.5" fontFamily={MONO} fill={C.muted}>
        {tx("news-daily.example/iq-game")}
      </text>
      <line x1="16" y1="62.5" x2="344" y2="62.5" stroke={C.line} />

      <AppChip x={16} y={70} />
      <text x="48" y="87" fontSize="13" fontWeight="700" fill={C.text}>
        {tx("Daily News")}
      </text>

      <rect x="16.5" y="102.5" width="327" height="44" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="29" y="121" fontSize="12.5" fontWeight="700" fill={C.text}>
        {tx("Take the 60-second IQ test")}
      </text>
      <text x="29" y="136" fontSize="10.5" fill={C.muted}>
        {tx("Beat the average score")}
      </text>
      <rect x="270.5" y="113.5" width="62" height="22" rx="11" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="301.5" y="128.5" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Play")}
      </text>

      <rect x="16.5" y="158.5" width="327" height="124" rx="10" fill={C.code} stroke={C.line} />
      <circle cx="40" cy="184" r="15" fill={C.amber} fillOpacity="0.12" />
      <g transform="translate(40 184)" fill="none" stroke={C.amber} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M-6 4 H6 M-4.5 4 V-1.5 a4.5 4.5 0 0 1 9 0 V4" />
        <path d="M-1.6 7.2 a1.8 1.8 0 0 0 3.2 0" />
      </g>
      <text x="64" y="181" fontSize="10.5" fill={C.muted}>
        {tx("iq-game.example wants to")}
      </text>
      <text x="64" y="199" fontSize="13" fontWeight="700" fill={C.text}>
        {tx("Send you notifications")}
      </text>
      <line x1="17" y1="214.5" x2="343" y2="214.5" stroke={C.line} />

      <rect x="29.5" y="224.5" width="140" height="30" rx="8" fill={C.panel2} stroke={C.line} />
      <text x="99.5" y="244" fontSize="12" fontWeight="600" fill={C.soft} textAnchor="middle">
        {tx("Allow")}
      </text>
      <rect className="sr-anim-pulse" x="191.5" y="224.5" width="140" height="30" rx="8" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.5" style={{ transformOrigin: "261.5px 239.5px" }} />
      <text x="261.5" y="244" fontSize="12" fontWeight="700" fill={C.mint} textAnchor="middle">
        {tx("Block")}
      </text>
      <text x="29" y="272" fontSize="10.5" fill={C.muted}>
        {tx("Blocking is safe. The game still works.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. the manufactured repair: steps, a command, and a stop note       */
/* ------------------------------------------------------------------ */
function RepairStep({ y, n, label }: { y: number; n: string; label: string }): JSX.Element {
  return (
    <g>
      <circle cx="26" cy={y} r="8" fill={C.panel2} stroke={C.line} />
      <text x="26" y={y + 3.6} fontSize="10" fontWeight="700" fill={C.muted} textAnchor="middle">
        {txv(n)}
      </text>
      <text x="42" y={y + 4.2} fontSize="12" fill={C.text}>
        {txv(label)}
      </text>
    </g>
  );
}

function RepairArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label={tx("Example: a repair a game page asks for")} aria={tx("A game page claiming the browser needs repair, with steps to paste a command, and a note saying not to run it")} height={H}>
      <path d="M33 41 l7.5 13 h-15 z" fill="none" stroke={C.amber} strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="33" y1="45.6" x2="33" y2="49.4" stroke={C.amber} strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="33" cy="51.6" r="0.9" fill={C.amber} />
      <text x="50" y="52" fontSize="13" fontWeight="700" fill={C.text}>
        {tx("Game paused: repair your browser")}
      </text>
      <line x1="16" y1="66.5" x2="344" y2="66.5" stroke={C.line} />

      <text x="16" y="84" fontSize="11" fill={C.muted}>
        {tx("To keep your progress, follow these steps:")}
      </text>
      <RepairStep y={102} n="1" label={tx("Press the Windows key + R")} />
      <RepairStep y={126} n="2" label={tx("Paste the code below and press Enter")} />
      <RepairStep y={150} n="3" label={tx("Return to the game")} />

      <rect x="16.5" y="168.5" width="327" height="38" rx="8" fill={C.code} stroke={C.line} />
      <text x="29" y="192" fontSize="11.5" fontFamily={MONO} fill={C.soft}>
        <tspan fill={C.mint}>&gt;</tspan> {tx("cmd /c repair-game.bat")}
      </text>
      <rect className="sr-anim-blink" x="197" y="181" width="1.5" height="15" rx="0.75" fill={C.mint} />

      <rect x="16.5" y="218.5" width="327" height="70" rx="8" fill={C.red} fillOpacity="0.08" stroke={C.red} strokeOpacity="0.4" />
      <rect x="16" y="224" width="3" height="59" rx="1.5" fill={C.red} />
      <circle className="sr-anim-ring" cx="33" cy="239.5" r="5.5" fill="none" stroke={C.red} strokeWidth="1.5" />
      <circle cx="33" cy="239.5" r="1.8" fill={C.red} />
      <text x="46" y="243.5" fontSize="12.5" fontWeight="700" fill={C.red}>
        {tx("Do not run it")}
      </text>
      <text x="32" y="262" fontSize="11" fill={C.muted}>
        {tx("A game site cannot fix your browser.")}
      </text>
      <text x="32" y="278" fontSize="11" fill={C.muted}>
        {tx("Close the page. Tell IT if you already ran it.")}
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. glossary: grid of icon chips                                     */
/* ------------------------------------------------------------------ */
type Glyph = "malware" | "notify" | "extension" | "fake" | "command" | "report";

function GlyphShape({ g }: { g: Glyph }): JSX.Element {
  const s = { fill: "none", stroke: C.mint, strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" } as const;
  switch (g) {
    case "malware":
      return (
        <g {...s}>
          <ellipse cx="0" cy="1.5" rx="4" ry="5.5" />
          <circle cx="0" cy="-5.5" r="2.2" />
          <line x1="-4" y1="-1" x2="-8" y2="-3" />
          <line x1="4" y1="-1" x2="8" y2="-3" />
          <line x1="-4" y1="2" x2="-8.5" y2="2" />
          <line x1="4" y1="2" x2="8.5" y2="2" />
          <line x1="-3.6" y1="5.5" x2="-7.5" y2="8" />
          <line x1="3.6" y1="5.5" x2="7.5" y2="8" />
        </g>
      );
    case "notify":
      return (
        <g {...s}>
          <path d="M-6 4 H6 M-4.5 4 V-1.5 a4.5 4.5 0 0 1 9 0 V4" />
          <path d="M-1.6 7.2 a1.8 1.8 0 0 0 3.2 0" />
        </g>
      );
    case "extension":
      return (
        <g {...s}>
          <rect x="-6" y="-3" width="12" height="11" rx="2" />
          <circle cx="0" cy="-6" r="2.6" />
        </g>
      );
    case "fake":
      return (
        <g {...s}>
          <path d="M0 -8 l8 14 h-16 z" />
          <line x1="0" y1="-3" x2="0" y2="1.5" />
          <circle cx="0" cy="3.7" r="0.6" />
        </g>
      );
    case "command":
      return (
        <g {...s}>
          <rect x="-9" y="-7" width="18" height="14" rx="2.5" />
          <polyline points="-5,-2.5 -1.5,0 -5,2.5" />
          <line x1="1" y1="3" x2="5" y2="3" />
        </g>
      );
    case "report":
      return (
        <g {...s}>
          <path d="M-8 -6 H8 V3 H-1 L-5 7 V3 H-8 Z" />
          <line x1="-4" y1="-1.5" x2="4" y2="-1.5" />
        </g>
      );
  }
}

interface ChipSpec {
  g: Glyph;
  title: string;
  sub: string;
}

const CHIPS: ChipSpec[] = [
  { g: "malware", title: "Malware", sub: "harmful software" },
  { g: "notify", title: "Notifications", sub: "alerts from a site" },
  { g: "extension", title: "Extension", sub: "add-on with access" },
  { g: "fake", title: "Fake error", sub: "a scare tactic" },
  { g: "command", title: "Command tool", sub: "typed text runs" },
  { g: "report", title: "Tell IT", sub: "report it early" },
];

function TermsArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label={tx("Terms at a glance")} aria={tx("Six terms shown as labelled icon chips: malware, notifications, extension, fake error, command tool and tell IT")} height={H}>
      {CHIPS.map((c, i) => {
        const last = i === CHIPS.length - 1;
        const x = 16 + (i % 2) * 172;
        const y = 38 + Math.floor(i / 2) * 84;
        return (
          <g key={c.g} transform={`translate(${x} ${y})`}>
            <rect x="0.5" y="0.5" width="155" height="71" rx="10" fill={C.panel2} stroke={C.line} />
            <circle className={last ? "sr-anim-pulse" : undefined} cx="26" cy="36" r="16" fill={C.mint} fillOpacity="0.12" />
            <g transform="translate(26 36)">
              <GlyphShape g={c.g} />
            </g>
            <text x="51" y="33" fontSize="12.5" fontWeight="700" fill={C.text}>
              {txv(c.title)}
            </text>
            <text x="51" y="50" fontSize="10.5" fill={C.muted}>
              {txv(c.sub)}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const MALWARE_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Opening a page is safe. Saying yes is the risk.",
    illustration: PermissionArt,
    caption: "Allow, install, run: you answer each one.",
    sections: [
      {
        h: "What this is about",
        body: "A normal work morning. A game ad on a news site asks you to allow, add and run things. You decide each time what to allow, install or run.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who uses a browser at work. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Malware needs a yes from you to run. Company controls help but can miss it. A page can ask. Only you can say no.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "Three small yeses, one big problem.",
    illustration: RepairArt,
    caption: "A game site does not repair browsers.",
    sections: [
      {
        h: "Each ask is bigger",
        body: "Notifications first, then an extension, then a repair. A yes to the small ask makes the next one feel normal.",
      },
      {
        h: "Extensions see everything",
        body: "One that reads every site you visit also sees your work logins and mail.",
      },
      {
        h: "The repair is the trap",
        body: "A made-up error tells you to paste commands. Running them is what installs the malware.",
      },
      {
        h: "What to do instead",
        body: "Block it, don\u2019t add it, close the page. If you already clicked or ran something, tell IT at once.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: TermsArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Malware", body: "Software built to harm, spy or steal. It only does damage once it runs." },
      { h: "Notification permission", body: "Lets a site send you alerts later, even after you leave. Block it and the page still works." },
      { h: "Browser extension", body: "An add-on with its own access. One that reads every site also sees your work accounts." },
      { h: "Fake error", body: "A warning a page invents to scare you into acting. Real fixes never come from a game site." },
      { h: "Command tool", body: "A window where typed text runs as commands. Never paste in text a web page gave you." },
      { h: "Tell IT", body: "Report it even if you only tried one step. Early notice limits the harm." },
    ],
  },
];
